/**
 * PaginationFooter — shared pagination bar.
 * Left: "1–{end} of {total}" (or "No results").
 * Right: prev/next controls. By default (audit modal) a "Page {x}/{y}" label
 * sits between them; when `onPageSelect` is provided the caller opts into
 * numbered page buttons + a highlighted current page ("standard pagination").
 *
 * Extracted from WorkshopAuditLogModal's local PaginationFooter (BON-214 slice
 * 4). The default (no `onPageSelect`) rendering is byte-for-byte identical to
 * the original, so the audit-log caller is unaffected.
 */

interface Props {
  /** Zero-based offset of the first item on this page. */
  offset: number;
  /** Total item count across all pages. */
  total: number;
  /** Number of items actually returned on this page (may be < pageSize on last page). */
  count: number;
  /** 1-based current page number. */
  currentPage: number;
  /** Total number of pages. */
  pageCount: number;
  /** Whether a previous page exists. */
  hasPrev: boolean;
  /** Whether a next page exists. */
  hasNext: boolean;
  onPrev: () => void;
  onNext: () => void;
  /** Opt-in: when provided, render numbered page buttons (jump to a page) in
   *  place of the plain "Page x/y" label. Omit for prev/next-only (audit). */
  onPageSelect?: (page: number) => void;
}

/**
 * The set of page numbers to render, collapsing long runs to a `'gap'` marker.
 * Always keeps first, last, and the current ± 1 window. Pure — exported for tests.
 */
export function pageWindow(currentPage: number, pageCount: number): (number | 'gap')[] {
  if (pageCount <= 7) return Array.from({ length: pageCount }, (_, i) => i + 1);
  const wanted = [1, pageCount, currentPage, currentPage - 1, currentPage + 1];
  const shown = [...new Set(wanted)].filter((p) => p >= 1 && p <= pageCount).sort((a, b) => a - b);
  const out: (number | 'gap')[] = [];
  let prev = 0;
  for (const p of shown) {
    if (p - prev > 1) out.push('gap');
    out.push(p);
    prev = p;
  }
  return out;
}

function NumberedPages({
  currentPage,
  pageCount,
  onPageSelect,
}: {
  currentPage: number;
  pageCount: number;
  onPageSelect: (page: number) => void;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      {pageWindow(currentPage, pageCount).map((p, i) =>
        p === 'gap' ? (
          <span key={`gap-${i}`} style={{ color: 'var(--nb-subtle)', padding: '0 2px' }}>
            …
          </span>
        ) : (
          <button
            key={p}
            type="button"
            className="nb-btn"
            aria-label={`Go to page ${p}`}
            aria-current={p === currentPage ? 'page' : undefined}
            onClick={() => onPageSelect(p)}
            style={
              p === currentPage
                ? {
                    fontSize: 11.5,
                    fontWeight: 700,
                    borderColor: 'var(--nb-primary)',
                    color: 'var(--nb-primary)',
                  }
                : { fontSize: 11.5 }
            }
          >
            {p}
          </button>
        ),
      )}
    </div>
  );
}

export function PaginationFooter({
  offset,
  total,
  count,
  currentPage,
  pageCount,
  hasPrev,
  hasNext,
  onPrev,
  onNext,
  onPageSelect,
}: Props) {
  const from = total === 0 ? 0 : offset + 1;
  const to = offset + count;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '10px 16px',
        borderTop: '1px solid var(--nb-border)',
        fontSize: 11.5,
        color: 'var(--nb-subtle)',
      }}
    >
      <span>{total === 0 ? 'No results' : `${from}–${to} of ${total}`}</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        {hasPrev && (
          <button type="button" className="nb-btn" style={{ fontSize: 11.5 }} onClick={onPrev}>
            ‹ Prev
          </button>
        )}
        {onPageSelect ? (
          <NumberedPages
            currentPage={currentPage}
            pageCount={pageCount}
            onPageSelect={onPageSelect}
          />
        ) : (
          (hasPrev || hasNext) && (
            <span style={{ fontSize: 11, color: 'var(--nb-subtle)' }}>
              Page {currentPage}/{pageCount}
            </span>
          )
        )}
        {hasNext && (
          <button type="button" className="nb-btn" style={{ fontSize: 11.5 }} onClick={onNext}>
            Next ›
          </button>
        )}
      </div>
    </div>
  );
}
